import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  CircularProgress,
  Collapse,
  Chip,
  Stack
} from '@mui/material';
import {
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { es } from 'date-fns/locale';

// Esquemas de validación según el estado
const validationSchemas = {
  CREATED: Yup.object({
    company: Yup.string().required('La empresa es requerida'),
    providerId: Yup.string().required('El proveedor es requerido'),
    invoiceDate: Yup.date().required('La fecha de factura es requerida'),
    // baseAmount se genera a partir de las líneas
    baseAmount: Yup.number()
      .required('Debe haber al menos una línea con importe')
      .min(0.01, 'El total base debe ser mayor a 0'),
    items: Yup.array().of(
      Yup.object({
        name: Yup.string().required('Nombre requerido'),
        base: Yup.number().typeError('Número inválido').min(0.01, 'Debe ser > 0').required('Base requerida'),
        taxes: Yup.array().of(Yup.number()).min(1, 'Debe tener al menos un impuesto')
      })
    ).min(1, 'Debe agregar al menos un producto')
  }),

  PENDING_SIGNATURE: Yup.object({
    // No se requieren campos adicionales, solo confirmación
  }),

  PENDING_TRANSFER: Yup.object({
    paymentMethod: Yup.string()
      .required('El método de pago es requerido')
      .oneOf(['Contado', 'Transferencia', 'Recibo a la vista'], 'Método de pago no válido'),
    // Campos condicionales para métodos de pago inmediato
    paymentDate: Yup.date().when('paymentMethod', {
      is: (value) => value === 'Contado' || value === 'Recibo a la vista',
      then: (schema) => schema.required('La fecha de pago es requerida para este método de pago')
        .max(new Date(), 'La fecha de pago no puede ser futura'),
      otherwise: (schema) => schema.nullable()
    }),
    comments: Yup.string().when('paymentMethod', {
      is: (value) => value === 'Contado' || value === 'Recibo a la vista',
      then: (schema) => schema.max(500, 'Los comentarios no pueden exceder los 500 caracteres'),
      otherwise: (schema) => schema.nullable()
    })
  }),

  PENDING_PAYMENT: Yup.object({
    paymentDate: Yup.date()
      .required('La fecha de pago es requerida')
      .max(new Date(), 'La fecha de pago no puede ser futura'),
    comments: Yup.string().max(500, 'Los comentarios no pueden exceder los 500 caracteres')
  }),

  CANCELLED: Yup.object({
    reason: Yup.string()
      .required('El motivo de cancelación es requerido')
      .min(10, 'El motivo debe tener al menos 10 caracteres')
      .max(500, 'El motivo no puede exceder los 500 caracteres')
  })
};

// Empresas disponibles según análisis
const COMPANIES = [
  'Conlima SLU',
  'Conlima Contratas y Servicios SLU', 
  'Renting Grupo Hermes SLU'
];

// Valores iniciales según el estado
const getInitialValues = (status, orderData = null) => {
  const data = orderData || {};
  
  // Helper para validar fechas
  const validateDate = (dateValue) => {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? null : date;
  };
  
  switch (status) {
    case 'CREATED': {
      // Si viene de backend con items, convertir vatRate a taxes array
      if (data.items && data.items.length > 0) {
        const convertedItems = data.items.map(item => ({
          name: item.name || '',
          base: item.baseAmount || item.base || '',
          taxes: item.taxes || (item.vatRate ? [item.vatRate] : [21])
        }));
        return {
          company: data.company || '',
          providerId: data.providerId || '',
          invoiceDate: validateDate(data.invoiceDate),
          baseAmount: data.baseAmount || '',
          items: convertedItems
        };
      }
      
      // Si viene de backend sin items, inicializamos una línea vacía
      const initialItem = {
        name: 'Producto 1',
        base: data.baseAmount ? Number(data.baseAmount) : '',
        taxes: data.vatRate ? [data.vatRate] : [21]
      };
      return {
        company: data.company || '',
        providerId: data.providerId || '',
        invoiceDate: validateDate(data.invoiceDate),
        baseAmount: data.baseAmount || '',
        items: data.baseAmount ? [initialItem] : [ { name: '', base: '', taxes: [21] } ]
      };
    }
    case 'PENDING_SIGNATURE':
      return {}; // Sin campos adicionales
    case 'PENDING_TRANSFER':
      return {
        paymentMethod: data.paymentMethod || '',
        paymentDate: validateDate(data.paymentDate) || null,
        comments: data.comments || ''
      };
    case 'PENDING_PAYMENT':
      return {
        paymentDate: validateDate(data.paymentDate),
        comments: data.comments || ''
      };
    case 'CANCELLED':
      return {
        reason: data.reason || ''
      };
    default:
      return {};
  }
};

function OrderForm({
  status,
  orderData,
  providers,
  companies,
  onSubmit,
  onCancel,
  isSubmitting
}) {
  // Estado para controlar si se muestra el formulario de pago inmediato
  const [showImmediatePayment, setShowImmediatePayment] = useState(false);
  
  // Estado para gestionar tipos de impuestos personalizados
  const [customVatRates, setCustomVatRates] = useState([
    { id: 21, name: 'General', rate: 21 },
    { id: 10, name: 'Reducido', rate: 10 },
    { id: 4, name: 'Superreducido', rate: 4 }
  ]);
  const [newVatName, setNewVatName] = useState('');
  const [newVatRate, setNewVatRate] = useState('');

  const formik = useFormik({
    initialValues: getInitialValues(status, orderData),
    validationSchema: validationSchemas[status],
    enableReinitialize: true,
    onSubmit: (values) => {
      // Incluir customVatRates al enviar
      onSubmit({ ...values, customVatRates });
    }
  });

  // Recuperar customVatRates desde orderData cuando se carga un pedido
  useEffect(() => {
    if (orderData && orderData.logs) {
      // Buscar en el historial si hay customVatRates guardados
      const historyWithCustomRates = orderData.logs
        .filter(log => log.details?.newValues?.customVatRates)
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); // Más reciente primero
      
      if (historyWithCustomRates.length > 0) {
        const savedCustomRates = historyWithCustomRates[0].details.newValues.customVatRates;
        if (Array.isArray(savedCustomRates) && savedCustomRates.length > 0) {
          setCustomVatRates(savedCustomRates);
        }
      }
    }
  }, [orderData]);

  // Controlar la visibilidad del formulario de pago inmediato
  useEffect(() => {
    if (status === 'PENDING_TRANSFER' && formik.values.paymentMethod) {
      const isImmediatePayment = formik.values.paymentMethod === 'Contado' || 
                                 formik.values.paymentMethod === 'Recibo a la vista';
      setShowImmediatePayment(isImmediatePayment);
      
      // Si no es pago inmediato, limpiar los campos de fecha y comentarios
      if (!isImmediatePayment) {
        formik.setFieldValue('paymentDate', null, false);
        formik.setFieldValue('comments', '', false);
      } else {
        // Si es pago inmediato y no hay fecha, poner la fecha de hoy
        if (!formik.values.paymentDate) {
          formik.setFieldValue('paymentDate', new Date(), false);
        }
      }
    }
  }, [status, formik.values.paymentMethod]);

  // Sincronizar baseAmount derivado de items (solo CREATED)
  useEffect(() => {
    if (status === 'CREATED') {
      const items = formik.values.items || [];
      const validItems = items.filter(it => it.base && !isNaN(it.base));
      const totalBase = validItems.reduce((acc, it) => acc + parseFloat(it.base || 0), 0);
      formik.setFieldValue('baseAmount', totalBase.toFixed(2), false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, formik.values.items]);

  // Calcular el total con impuestos
  const calculateTotal = () => {
    if (status === 'CREATED') {
      const items = formik.values.items || [];
      let total = 0;
      items.forEach(item => {
        const baseVal = parseFloat(item.base || 0);
        if (!isNaN(baseVal) && item.taxes && item.taxes.length > 0) {
          const totalTaxRate = item.taxes.reduce((sum, tax) => sum + tax, 0);
          total += baseVal * (1 + totalTaxRate / 100);
        }
      });
      return total.toFixed(2);
    }
    return null;
  };

  // Handlers para items
  const handleAddItem = () => {
    const lastItem = formik.values.items?.[formik.values.items.length - 1];
    const defaultTaxes = lastItem?.taxes && lastItem.taxes.length > 0 ? [...lastItem.taxes] : [21];
    const newItems = [...(formik.values.items || []), { name: '', base: '', taxes: defaultTaxes }];
    formik.setFieldValue('items', newItems);
  };

  const handleRemoveItem = (index) => {
    const newItems = (formik.values.items || []).filter((_, i) => i !== index);
    formik.setFieldValue('items', newItems.length ? newItems : [{ name: '', base: '', taxes: [21] }]);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...(formik.values.items || [])];
    newItems[index] = { ...newItems[index], [field]: field === 'base' ? value : value };
    formik.setFieldValue('items', newItems);
  };

  const handleDuplicateItem = (index) => {
    const items = formik.values.items || [];
    const dup = { ...items[index], taxes: [...(items[index].taxes || [21])] };
    const newItems = [...items.slice(0, index + 1), dup, ...items.slice(index + 1)];
    formik.setFieldValue('items', newItems);
  };

  // Handlers para agregar/quitar impuestos a items
  const handleAddTaxToItem = (itemIndex, taxRate) => {
    const newItems = [...(formik.values.items || [])];
    const currentTaxes = newItems[itemIndex].taxes || [];
    if (!currentTaxes.includes(taxRate)) {
      newItems[itemIndex] = { 
        ...newItems[itemIndex], 
        taxes: [...currentTaxes, taxRate] 
      };
      formik.setFieldValue('items', newItems);
    }
  };

  const handleRemoveTaxFromItem = (itemIndex, taxRate) => {
    const newItems = [...(formik.values.items || [])];
    const currentTaxes = newItems[itemIndex].taxes || [];
    // No permitir eliminar el último impuesto
    if (currentTaxes.length > 1) {
      newItems[itemIndex] = { 
        ...newItems[itemIndex], 
        taxes: currentTaxes.filter(t => t !== taxRate) 
      };
      formik.setFieldValue('items', newItems);
    }
  };

  // Handlers para tipos de impuestos personalizados
  const handleAddCustomVat = () => {
    if (!newVatName.trim() || !newVatRate || isNaN(parseFloat(newVatRate))) {
      return;
    }
    
    const rate = parseFloat(newVatRate);
    if (rate < 0 || rate > 100) {
      return;
    }
    
    // Verificar si ya existe
    if (customVatRates.some(v => v.rate === rate)) {
      return;
    }
    
    const newVat = {
      id: rate,
      name: newVatName.trim(),
      rate: rate
    };
    
    setCustomVatRates([...customVatRates, newVat]);
    setNewVatName('');
    setNewVatRate('');
  };
  
  const handleRemoveCustomVat = (rateToRemove) => {
    // No permitir eliminar los tipos predefinidos (21, 10, 4)
    if ([21, 10, 4].includes(rateToRemove)) {
      return;
    }
    
    // Verificar si el impuesto está siendo usado en algún producto
    const isInUse = (formik.values.items || []).some(item => 
      item.taxes && item.taxes.includes(rateToRemove)
    );
    
    if (isInUse) {
      // Mostrar error o alerta (podrías usar un snackbar o alert)
      alert('No se puede eliminar este impuesto porque está siendo usado en uno o más productos.');
      return;
    }
    
    setCustomVatRates(customVatRates.filter(v => v.rate !== rateToRemove));
  };

  const calculateItemTotal = (item) => {
    const baseVal = parseFloat(item.base || 0);
    const taxes = item.taxes || [];
    if (isNaN(baseVal) || taxes.length === 0) return 0;
    const totalTaxRate = taxes.reduce((sum, tax) => sum + tax, 0);
    return baseVal * (1 + totalTaxRate / 100);
  };

  const vatBreakdown = () => {
    const map = {};
    (formik.values.items || []).forEach(it => {
      const baseVal = parseFloat(it.base || 0);
      const taxes = it.taxes || [];
      if (!baseVal || isNaN(baseVal) || taxes.length === 0) return;
      
      taxes.forEach(taxRate => {
        if (!map[taxRate]) map[taxRate] = { base: 0, totalVat: 0 };
        map[taxRate].base += baseVal;
        map[taxRate].totalVat += baseVal * (taxRate / 100);
      });
    });
    return map; // {21:{base:..., totalVat:...}, ...}
  };

  // Renderizar el formulario según el estado
  const renderForm = () => {
    switch (status) {
      case 'CREATED':
        return (
          <Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth error={formik.touched.company && Boolean(formik.errors.company)}>
                  <InputLabel>Empresa</InputLabel>
                  <Select
                    name="company"
                    value={formik.values.company}
                    label="Empresa"
                    onChange={formik.handleChange}
                  >
                    {COMPANIES.map((company) => (
                      <MenuItem key={company} value={company}>
                        {company}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.company && formik.errors.company && (
                    <FormHelperText>{formik.errors.company}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth error={formik.touched.providerId && Boolean(formik.errors.providerId)}>
                  <InputLabel>Proveedor</InputLabel>
                  <Select
                    name="providerId"
                    value={formik.values.providerId}
                    label="Proveedor"
                    onChange={formik.handleChange}
                  >
                    <MenuItem value="">
                      <em>Seleccionar proveedor...</em>
                    </MenuItem>
                    {providers.map((provider) => (
                      <MenuItem key={provider.id} value={provider.id}>
                        {provider.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.providerId && formik.errors.providerId && (
                    <FormHelperText>{formik.errors.providerId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <DatePicker
                    label="Fecha de Factura"
                    value={formik.values.invoiceDate}
                    onChange={(date) => {
                      if (date && !isNaN(new Date(date).getTime())) {
                        formik.setFieldValue('invoiceDate', date);
                      } else {
                        formik.setFieldValue('invoiceDate', null);
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: formik.touched.invoiceDate && Boolean(formik.errors.invoiceDate),
                        helperText: formik.touched.invoiceDate && formik.errors.invoiceDate
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ mb: 1 }}>Productos / Conceptos</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell width={140}>Base (€)</TableCell>
                  <TableCell width={250}>Impuestos</TableCell>
                  <TableCell width={120}>Total (€)</TableCell>
                  <TableCell width={90} align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(formik.values.items || []).map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        variant="standard"
                        fullWidth
                        value={item.name}
                        onChange={(e) => handleItemChange(idx, 'name', e.target.value)}
                        placeholder={`Producto ${idx+1}`}
                        error={Boolean(formik.errors.items?.[idx]?.name)}
                        helperText={formik.errors.items?.[idx]?.name}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        fullWidth
                        type="number"
                        value={item.base}
                        onChange={(e) => handleItemChange(idx, 'base', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && idx === (formik.values.items?.length || 0) - 1) {
                            e.preventDefault();
                            handleAddItem();
                          }
                        }}
                        error={Boolean(formik.errors.items?.[idx]?.base)}
                        helperText={formik.errors.items?.[idx]?.base}
                        inputProps={{ min: 0, step: '0.01' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {/* Impuestos aplicados */}
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {(item.taxes || []).map((taxRate, taxIdx) => {
                            const taxInfo = customVatRates.find(v => v.rate === taxRate);
                            return (
                              <Chip
                                key={taxIdx}
                                label={`${taxInfo?.name || taxRate + '%'}`}
                                size="small"
                                onDelete={(item.taxes || []).length > 1 ? () => handleRemoveTaxFromItem(idx, taxRate) : undefined}
                                color="primary"
                                variant="outlined"
                              />
                            );
                          })}
                        </Stack>
                        {/* Selector para agregar impuestos */}
                        <FormControl variant="standard" size="small">
                          <Select
                            displayEmpty
                            value=""
                            onChange={(e) => {
                              if (e.target.value) {
                                handleAddTaxToItem(idx, e.target.value);
                              }
                            }}
                            renderValue={() => '+ Agregar impuesto'}
                          >
                            <MenuItem value="" disabled>
                              <em>Seleccionar impuesto...</em>
                            </MenuItem>
                            {customVatRates
                              .filter(vat => !(item.taxes || []).includes(vat.rate))
                              .map(vat => (
                                <MenuItem key={vat.id} value={vat.rate}>
                                  {vat.name} ({vat.rate}%)
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                        {formik.errors.items?.[idx]?.taxes && (
                          <FormHelperText error>{formik.errors.items[idx].taxes}</FormHelperText>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{calculateItemTotal(item).toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Duplicar línea">
                        <IconButton size="small" onClick={() => handleDuplicateItem(idx)}>
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar línea">
                        <span>
                          <IconButton size="small" onClick={() => handleRemoveItem(idx)} disabled={(formik.values.items || []).length === 1}>
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={5}>
                    <Button startIcon={<AddIcon />} size="small" onClick={handleAddItem}>
                      Añadir línea
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            {formik.errors.items && typeof formik.errors.items === 'string' && (
              <FormHelperText error sx={{ mt: 1 }}>{formik.errors.items}</FormHelperText>
            )}

            {/* Tabla de gestión de tipos de impuestos personalizados */}
            <Box sx={{ mt: 4, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Tipos de Impuestos Disponibles
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Puedes agregar tipos de impuestos personalizados (IVA, IGIC, IPSI, etc.) que estarán disponibles en el desplegable de cada producto.
              </Typography>
              
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell width={120}>Porcentaje (%)</TableCell>
                    <TableCell width={90} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customVatRates.map((vat) => (
                    <TableRow key={vat.id}>
                      <TableCell>{vat.name}</TableCell>
                      <TableCell>{vat.rate}%</TableCell>
                      <TableCell align="center">
                        {![21, 10, 4].includes(vat.rate) && (
                          <Tooltip title="Eliminar tipo de impuesto">
                            <IconButton size="small" onClick={() => handleRemoveCustomVat(vat.rate)}>
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <TextField
                        variant="standard"
                        fullWidth
                        placeholder="Ej: IGIC General, IPSI Reducido"
                        value={newVatName}
                        onChange={(e) => setNewVatName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCustomVat();
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        fullWidth
                        type="number"
                        placeholder="Ej: 5"
                        value={newVatRate}
                        onChange={(e) => setNewVatRate(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCustomVat();
                          }
                        }}
                        inputProps={{ min: 0, max: 100, step: '0.01' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Agregar tipo de impuesto">
                        <span>
                          <IconButton 
                            size="small" 
                            onClick={handleAddCustomVat}
                            disabled={!newVatName.trim() || !newVatRate || isNaN(parseFloat(newVatRate))}
                            color="primary"
                          >
                            <AddIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Box>
        );

      case 'PENDING_SIGNATURE':
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Revisión del Pedido</strong>
              </Typography>
              <Typography variant="body2">
                Revisa los detalles del pedido y procede a aprobar o cancelar.
                Esta acción quedará registrada en el historial del pedido.
              </Typography>
            </Alert>

            {orderData && (
              <Paper sx={{ p: 3, backgroundColor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>
                  Resumen del Pedido
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Empresa:</strong> {orderData.company || 'No asignada'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Proveedor:</strong> {orderData.provider?.name || 'No asignado'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Fecha Factura:</strong> {
                        orderData.invoiceDate 
                          ? new Date(orderData.invoiceDate).toLocaleDateString('es-ES')
                          : 'No definida'
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Total:</strong> {
                        orderData.amount && !isNaN(Number(orderData.amount))
                          ? Number(orderData.amount).toFixed(2)
                          : '0.00'
                      } €
                    </Typography>
                  </Grid>
                  {orderData.items && orderData.items.length > 0 && (
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>Productos:</Typography>
                        {orderData.items.map((it, idx) => {
                          const taxes = it.taxes || (it.vatRate ? [it.vatRate] : []);
                          const taxNames = taxes.map(tax => {
                            const taxInfo = customVatRates.find(v => v.rate === tax);
                            return taxInfo ? `${taxInfo.name} (${tax}%)` : `${tax}%`;
                          }).join(' + ');
                          const totalTaxRate = taxes.reduce((sum, t) => sum + t, 0);
                          
                          return (
                            <Typography key={it.id || idx} variant="caption" display="block" sx={{ mb: 0.5 }}>
                              • <strong>{it.name}</strong>
                              <br />
                              &nbsp;&nbsp;Base: {Number(it.baseAmount || it.base || 0).toFixed(2)} € | 
                              Impuestos: {taxNames} ({totalTaxRate}% total) | 
                              Total: {Number(it.totalAmount || 0).toFixed(2)} €
                            </Typography>
                          );
                        })}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}
          </Box>
        );

      case 'PENDING_TRANSFER':
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Define el método de pago para continuar con el procesamiento del pedido.
              </Typography>
            </Alert>

            <FormControl fullWidth error={formik.touched.paymentMethod && Boolean(formik.errors.paymentMethod)} sx={{ mb: 3 }}>
              <InputLabel>Método de Pago</InputLabel>
              <Select
                name="paymentMethod"
                value={formik.values.paymentMethod}
                label="Método de Pago"
                onChange={formik.handleChange}
              >
                <MenuItem value="Contado">Contado</MenuItem>
                <MenuItem value="Transferencia">Transferencia</MenuItem>
                <MenuItem value="Recibo a la vista">Recibo a la vista</MenuItem>
              </Select>
              {formik.touched.paymentMethod && formik.errors.paymentMethod && (
                <FormHelperText>{formik.errors.paymentMethod}</FormHelperText>
              )}
            </FormControl>

            {/* Formulario de pago inmediato para Contado y Recibo a la vista */}
            <Collapse in={showImmediatePayment} timeout={300}>
              <Alert 
                severity="warning" 
                sx={{ mb: 3 }}
              >
                <Typography variant="body2" gutterBottom>
                  <strong>Pago Inmediato Requerido</strong>
                </Typography>
                <Typography variant="body2">
                  Para el método de pago "{formik.values.paymentMethod}", debe registrar la fecha de pago. 
                  El pedido será marcado como completado directamente.
                </Typography>
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                    <DatePicker
                      label="Fecha de Pago *"
                      value={formik.values.paymentDate}
                      onChange={(date) => {
                        if (date && !isNaN(new Date(date).getTime())) {
                          formik.setFieldValue('paymentDate', date);
                        } else {
                          formik.setFieldValue('paymentDate', null);
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: formik.touched.paymentDate && Boolean(formik.errors.paymentDate),
                          helperText: formik.touched.paymentDate && formik.errors.paymentDate
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    name="comments"
                    label="Comentarios (opcional)"
                    placeholder="Añade cualquier comentario sobre el pago..."
                    value={formik.values.comments}
                    onChange={formik.handleChange}
                    error={formik.touched.comments && Boolean(formik.errors.comments)}
                    helperText={formik.touched.comments && formik.errors.comments}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Box>
        );

      case 'PENDING_PAYMENT':
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Registra la fecha en que se realizó el pago y añade comentarios si es necesario.
              </Typography>
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <DatePicker
                    label="Fecha de Pago"
                    value={formik.values.paymentDate}
                    onChange={(date) => {
                      // Validar que la fecha sea válida antes de establecerla
                      if (date && !isNaN(new Date(date).getTime())) {
                        formik.setFieldValue('paymentDate', date);
                      } else {
                        formik.setFieldValue('paymentDate', null);
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: formik.touched.paymentDate && Boolean(formik.errors.paymentDate),
                        helperText: formik.touched.paymentDate && formik.errors.paymentDate
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  name="comments"
                  label="Comentarios (opcional)"
                  placeholder="Añade cualquier comentario sobre el pago..."
                  value={formik.values.comments}
                  onChange={formik.handleChange}
                  error={formik.touched.comments && Boolean(formik.errors.comments)}
                  helperText={formik.touched.comments && formik.errors.comments}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 'CANCELLED':
        return (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Este pedido ha sido cancelado.
              </Typography>
            </Alert>
            {orderData?.cancelReason && (
              <Paper sx={{ p: 2, backgroundColor: 'grey.50', mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Motivo de cancelación:</strong>
                </Typography>
                <Typography variant="body2">
                  {orderData.cancelReason}
                </Typography>
              </Paper>
            )}
            {/* Mostrar resumen del pedido cancelado */}
            {orderData && (
              <Paper sx={{ p: 3, backgroundColor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>
                  Resumen del Pedido
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Empresa:</strong> {orderData.company || 'No asignada'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Proveedor:</strong> {orderData.provider?.name || 'No asignado'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Fecha Factura:</strong> {
                        orderData.invoiceDate 
                          ? new Date(orderData.invoiceDate).toLocaleDateString('es-ES')
                          : 'No definida'
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Total:</strong> {
                        orderData.amount && !isNaN(Number(orderData.amount))
                          ? Number(orderData.amount).toFixed(2)
                          : '0.00'
                      } €
                    </Typography>
                  </Grid>
                  {orderData.items && orderData.items.length > 0 && (
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>Productos:</Typography>
                        {orderData.items.map((it, idx) => {
                          const taxes = it.taxes || (it.vatRate ? [it.vatRate] : []);
                          const taxNames = taxes.map(tax => {
                            const taxInfo = customVatRates.find(v => v.rate === tax);
                            return taxInfo ? `${taxInfo.name} (${tax}%)` : `${tax}%`;
                          }).join(' + ');
                          const totalTaxRate = taxes.reduce((sum, t) => sum + t, 0);
                          
                          return (
                            <Typography key={it.id || idx} variant="caption" display="block" sx={{ mb: 0.5 }}>
                              • <strong>{it.name}</strong>
                              <br />
                              &nbsp;&nbsp;Base: {Number(it.baseAmount || it.base || 0).toFixed(2)} € | 
                              Impuestos: {taxNames} ({totalTaxRate}% total) | 
                              Total: {Number(it.totalAmount || 0).toFixed(2)} €
                            </Typography>
                          );
                        })}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}
          </Box>
        );

      case 'COMPLETED':
        return (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Este pedido ha sido completado exitosamente.
              </Typography>
            </Alert>
            {/* Mostrar resumen del pedido completado */}
            {orderData && (
              <Paper sx={{ p: 3, backgroundColor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>
                  Resumen del Pedido
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Empresa:</strong> {orderData.company || 'No asignada'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Proveedor:</strong> {orderData.provider?.name || 'No asignado'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Fecha Factura:</strong> {
                        orderData.invoiceDate 
                          ? new Date(orderData.invoiceDate).toLocaleDateString('es-ES')
                          : 'No definida'
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Total:</strong> {
                        orderData.amount && !isNaN(Number(orderData.amount))
                          ? Number(orderData.amount).toFixed(2)
                          : '0.00'
                      } €
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Método de Pago:</strong> {orderData.paymentMethod || 'No definido'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Fecha de Pago:</strong> {
                        orderData.paymentDate 
                          ? new Date(orderData.paymentDate).toLocaleDateString('es-ES')
                          : 'No definida'
                      }
                    </Typography>
                  </Grid>
                  {orderData.items && orderData.items.length > 0 && (
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>Productos:</Typography>
                        {orderData.items.map((it, idx) => {
                          const taxes = it.taxes || (it.vatRate ? [it.vatRate] : []);
                          const taxNames = taxes.map(tax => {
                            const taxInfo = customVatRates.find(v => v.rate === tax);
                            return taxInfo ? `${taxInfo.name} (${tax}%)` : `${tax}%`;
                          }).join(' + ');
                          const totalTaxRate = taxes.reduce((sum, t) => sum + t, 0);
                          
                          return (
                            <Typography key={it.id || idx} variant="caption" display="block" sx={{ mb: 0.5 }}>
                              • <strong>{it.name}</strong>
                              <br />
                              &nbsp;&nbsp;Base: {Number(it.baseAmount || it.base || 0).toFixed(2)} € | 
                              Impuestos: {taxNames} ({totalTaxRate}% total) | 
                              Total: {Number(it.totalAmount || 0).toFixed(2)} €
                            </Typography>
                          );
                        })}
                      </Box>
                    </Grid>
                  )}
                  {orderData.comments && (
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>Comentarios:</Typography>
                        <Typography variant="body2">{orderData.comments}</Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <form onSubmit={formik.handleSubmit}>
        <Typography variant="h6" gutterBottom>
          {status === 'CREATED' && 'Completar Detalles del Pedido'}
          {status === 'PENDING_SIGNATURE' && 'Revisión y Aprobación'}
          {status === 'PENDING_TRANSFER' && 'Definir Método de Pago'}
          {status === 'PENDING_PAYMENT' && 'Registrar Pago'}
        </Typography>

        {renderForm()}

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          {status === 'COMPLETED' || status === 'CANCELLED' ? (
            // Para pedidos finalizados, mostrar solo botón cerrar
            <>
              <Alert severity={status === 'COMPLETED' ? 'success' : 'warning'} sx={{ width: '100%', mb: 2 }}>
                <Typography variant="body2">
                  {status === 'COMPLETED' 
                    ? 'Este pedido ha sido completado.'
                    : 'Este pedido ha sido cancelado.'
                  }
                </Typography>
              </Alert>
              <Button
                variant="contained"
                onClick={onCancel}
                color="primary"
              >
                Cerrar
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={status === 'PENDING_SIGNATURE' ? () => onSubmit({ cancel: true }) : onCancel}
                disabled={isSubmitting}
                color={status === 'CREATED' ? 'inherit' : 'error'}
              >
                {status === 'CREATED' ? 'Cerrar' : 'Cancelar Pedido'}
              </Button>
              
              {status !== 'PENDING_SIGNATURE' ? (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={isSubmitting && <CircularProgress size={20} />}
                >
                  {isSubmitting ? 'Guardando...' : (
                    status === 'CREATED' ? 'Guardar y Pasar a Firma' :
                    status === 'PENDING_TRANSFER' ? 
                      (showImmediatePayment ? 'Completar Pedido' : 'Continuar') :
                    status === 'PENDING_PAYMENT' ? 'Marcar como Pagado' :
                    'Guardar'
                  )}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => onSubmit({ approved: true })}
                  disabled={isSubmitting}
                  startIcon={isSubmitting && <CircularProgress size={20} />}
                >
                  {isSubmitting ? 'Aprobando...' : 'Aprobar Pedido'}
                </Button>
              )}
            </>
          )}
        </Box>
      </form>
    </Paper>
  );
}

export default OrderForm; 